import { BaseViewStore } from 'core/stores';
import { ChannelStore } from 'modules/channel/stores';
import { action, makeObservable } from 'mobx';
import { carrierTypes, lookupRouteName, sourceOfChange, viewStoreName } from 'common/constants';
import { CustomLabelForm } from 'modules/order/forms';
import { translate } from 'core/utils';
import { BaasicDropdownViewStore } from 'common/stores';
import { LookupStore } from 'modules/lookup/stores';
import _ from 'lodash';

class CustomLabelViewStore extends BaseViewStore {
	regex = /\{{2}[^{}]*\}{2}/g;

	form = new CustomLabelForm({
		onSuccess: async () => {
			await this.saveCustomLabel();
			this.modalParams.success();
		},
	});

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.channelId = modalParams.data.selectedItem ? modalParams.data.selectedItem.id : null;
		this.channelStore = new ChannelStore(rootStore);
		this.stateDropdownStore = new BaasicDropdownViewStore({
			lookupRoute: lookupRouteName.state,
			field: this.form.$('returnStateId'),
			store: new LookupStore(this.rootStore),
			itemTemplate: {
				value: 'countryStateCode',
				label: 'name',
			},
			onChangeCallback: this.onStateDropdownChange,
		});
		this.carrierTypeDropdownStore = new BaasicDropdownViewStore({
			lookupRoute: lookupRouteName.carrierType,
			field: this.form.$('carrierTypeId'),
			store: new LookupStore(this.rootStore),
			onChangeCallback: this.onCarrierTypeDropdownChange,
			initCallback: this.setSelectedCarrierOption,
			filterCallback: options => {
				return _.map(options.filter(o => ![carrierTypes.passport, carrierTypes.ltl].includes(o.name)), item => {
					// TODO: integrate Passport carrier type with custom labels; until then, exclude from custom label flow
					return {
						value: _.get(item, 'id'),
						label: _.get(item, 'name'),
						extra: item,
					};
				});
			},
			isRequired: false,
		});
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		let data = null;
		let customFields = null;

		if (this.modalParams.data.store_path === viewStoreName.channelListViewStore) {
			// if channel list page, fetch the channel
			this.channel = await this.channelStore.getChannel(this.channelId, {
				embed: [
					'customFieldInputs',
					'customFieldInputs.customFieldInputVariables',
					'customFieldInputs.customFieldInputVariables.customFieldVariable',
				],
			});
		} else {
			this.channel = this.modalParams.data.channel ? this.modalParams.data.channel : null; // edit page : create page
		}
		if (this.channel) {
			const { customFieldInputs, ...customFieldData } = this.channel;
			data = customFieldData;
		} else {
			if (this.modalParams.data.customLabel) {
				const { customFieldInputs, ...customFieldData } = this.modalParams.data.customLabel;
				customFields = customFieldInputs;
				data = customFieldData;
			}
		}
		if (data) {
			this.form.updateForm(data, true);
			if (data.useCustomReturnAddress === true) {
				this.stateDropdownStore.setValue({
					value: data.returnState,
					label: data.returnStateName,
				});
			}
			this.initialValues = data;
		}
		this.setAddressFieldsRules(data != null && data.useCustomReturnAddress === true);
		await this.initializeCustomFields(customFields);
		this.resume();
	};

	@action.bound
	setSelectedCarrierOption = dropdownStore => {
		if (dropdownStore.options.length > 0) {
			dropdownStore.setValue(dropdownStore.options[0]);
			this.onCarrierTypeDropdownChange();
		}
	};

	@action.bound
	onUseCustomReturnAddressChange = field => {
		this.setAddressFieldsRules(field.value);

		this.form.$('returnName').clear();
		this.form.$('returnPhone').clear();
		this.form.$('returnAddress').clear();
		this.form.$('returnCity').clear();
		this.form.$('returnZip').clear();
		this.form.$('returnStateId').clear();
		this.stateDropdownStore.setValue(null);

		// Revert back to default value if custom Address is false
		if (field.value === false) {
			this.updateLabelPreview();
		}
	};

	@action.bound
	setAddressFieldsRules = value => {
		const rule = value === true ? 'required' : '';

		this.form.$('returnName').set('rules', rule);
		this.form.$('returnPhone').set('rules', rule);
		this.form.$('returnAddress').set('rules', rule);
		this.form.$('returnCity').set('rules', rule);
		this.form.$('returnZip').set('rules', rule);
		this.form.$('returnStateId').set('rules', rule);
	};

	@action.bound
	initializeCustomFields = async values => {
		const customFields = _.get(this, 'channel.customFieldInputs') ? _.get(this, 'channel.customFieldInputs') : values;
		if (customFields && customFields.length > 0) {
			_.forEach(_.sortBy(customFields, 'fieldNumber'), item => {
				let fieldToAdd = {
					fields: {
						id: item.id,
						value: item.value,
						previousValue: item.value,
						fieldNumber: item.fieldNumber,
						displayDropdown: false,
						lastIndex: 0,
						customFieldInputVariables: [],
					},
				};

				_.forEach(item.customFieldInputVariables, variable => {
					fieldToAdd.fields.customFieldInputVariables.push({
						id: variable.id,
						customFieldVariableId: variable.customFieldVariableId,
						customFieldInputId: variable.customFieldInputId,
						position: variable.position,
						value: variable.value ? variable.value : variable.customFieldVariable.displayName,
					});
				});
				let field = this.form.$('customFieldInputs').add(fieldToAdd);
				this.handleCustomFieldDropdown(field);
				field.customFieldVariableDropdown.value = [];
				_.forEach(item.customFieldInputVariables, variable => {
					field.customFieldVariableDropdown.value.push({
						value: variable.customFieldVariable.id,
						label: variable.customFieldVariable.name,
						extra: variable.customFieldVariable,
					});
				});
			});
		}

		for (let i = 0; i < 4; i++) {
			if (
				this.form.$('customFieldInputs').value.length === 0 ||
				!this.form.$('customFieldInputs').value.find(input => input.fieldNumber === i + 1)
			) {
				let field = this.form.$('customFieldInputs').add({
					fields: {
						value: '',
						previousValue: '',
						fieldNumber: i + 1,
						displayDropdown: false,
						lastIndex: 0,
						customFieldInputVariables: [],
					},
				});
				this.handleCustomFieldDropdown(field);
			}
		}
	};

	@action.bound
	handleCustomFieldDropdown = field => {
		if (field.$('fieldNumber').value === 3 || field.$('fieldNumber').value === 4) {
			field.labelAddon = translate([`CUSTOMLABEL.LABELADDON${field.$('fieldNumber').value}`]);
		}
		field.$('value').set('label', `${translate(['CUSTOMLABEL.CUSTOMFIELD'])}${field.$('fieldNumber').value}`);
		field.setRef = reference => {
			field.inputRef = reference;
		};
		field.customFieldVariableDropdown = new BaasicDropdownViewStore({
			store: new LookupStore(this.rootStore),
			lookupRoute: lookupRouteName.customFieldVariable,
			onChangeCallback: value => {
				this.onCustomFieldDropdownChange(value, field);
			},
			isMulti: true,
			onBlur: () => this.onCustomFieldDropdownBlur(field),
		});
	};

	@action.bound
	saveCustomLabel = async () => {
		this.suspend();
		try {
			// Carrier type is only used for preview. It should not be sent to backend.
			const { returnStateId, carrierTypeId, ...data } = this.form.values();
			this.modalParams.data.customLabel = {
				returnStateName: returnStateId ? this.stateDropdownStore.value.label : '',
				returnState: returnStateId ? returnStateId : '',
				...data,
			};
			if (this.modalParams.data.store_path === viewStoreName.channelListViewStore || this.channel) {
				await this.channelStore.updateChannelCustomLabel(
					{
						channelId: this.channelId,
						...this.modalParams.data.customLabel,
					},
					sourceOfChange.customizeLabel
				);
				if (this.modalParams.data.store_path === viewStoreName.channelEditViewStore)
					await this.modalParams.data.getChannel();
			}
			this.rootStore.notificationStore.success(['CUSTOMLABEL.UPDATED']);
		} catch (error) {
			this.rootStore.notificationStore.error(['CUSTOMLABEL.ERROR']);
		}
		this.resume();
	};

	// reset to initial values
	@action.bound
	resetValues = async () => {
		this.form.reset();
		const customFieldInputsLength = this.form.$('customFieldInputs').value.length;
		for (let i = 0; i < customFieldInputsLength; i++) {
			this.form.$(`customFieldInputs[${i}]`).del();
		}
		await this.initialize();
		this.updateLabelPreview();
	};

	@action.bound
	onCustomFieldInputChange = async (event, field) => {
		let value = event.target.value;
		if (value !== field.$('previousValue').value) {
			let index = this.findIndexOfDifference(field.$('previousValue').value, value); // Returns the index at which the user typed, so that a variable can be inserted into a string in the correct place.
			field.$('lastIndex').set(index);
			field.$('displayDropdown').value = value[index] === '{' ? true : false;
		}
		var outputString = await this.handleDelete(value, field);
		field.$('previousValue').value = field.$('value').value;
		this.updateSvgElementById(`customField${field.value.fieldNumber}`, outputString);
	};

	@action.bound
	handleDelete = async (value, field) => {
		let previousVariables =
			field.$('customFieldInputVariables').value.length > 0
				? field.$('customFieldInputVariables').value.map(variable => `{{${variable.value}}}`)
				: [];
		let currentVariables = value.match(this.regex);
		if (!this.arraysEqual(previousVariables, currentVariables)) {
			let deletedVariables = previousVariables.filter(m => !currentVariables || !currentVariables.some(cm => m === cm));
			if (deletedVariables.length > 0) {
				return await this.deleteVariables(deletedVariables, field);
			}
		}
		return value;
	};

	@action.bound
	deleteVariables = async (deletedVariables, field) => {
		_.forEach(field.$('customFieldInputVariables').value, item => {
			if (deletedVariables.includes(`{{${item.value}}}`)) {
				let variablesArray = field.$('customFieldInputVariables').map(field => field);
				let key = variablesArray.find(variable => variable.value.value === item.value).key;
				field.$(`customFieldInputVariables`).del(key);
				if (field.customFieldVariableDropdown.value) {
					field.customFieldVariableDropdown.value = field.customFieldVariableDropdown.value.filter(
						o => o.extra.displayName != item.value
					);
				}
			}
		});

		const pattern = new RegExp(deletedVariables.join('|'), 'g');
		const outputString = field.$('previousValue').value.replace(pattern, ''); // "previousValue" is used because it contains variables before deletion, which allows replacing by pattern
		field.$('value').set(outputString);
		return outputString;
	};

	@action.bound
	arraysEqual = (arr1, arr2) => {
		if ((!arr1 && !arr2) || (!arr1 && arr2) || (arr1 && !arr2) || arr1.length !== arr2.length) return false;
		for (let i = 0; i < arr1.length; i++) {
			if (arr1[i] !== arr2[i]) return false;
		}
		return true;
	};

	@action.bound
	onSelectChange = (selectedOption, field) => {
		const firstPart = field.value.value.substring(0, field.$('lastIndex').value);
		const secondPart = field.value.value.substring(field.$('lastIndex').value + 1);
		const resultString = firstPart + `{{${selectedOption.extra.displayName}}}` + secondPart;
		field.$('value').set(resultString);
		field.$('previousValue').set(resultString);
		field.$('displayDropdown').set(false);

		let matches = resultString.match(this.regex);
		let index = matches.indexOf(`{{${selectedOption.extra.displayName}}}`);
		field.$('customFieldInputVariables').add({
			fields: {
				fieldNumber: field.value.fieldNumber,
				customFieldInputId: field.value.id,
				customFieldVariableId: selectedOption.value,
				position: index,
				value: selectedOption.extra.displayName,
			},
		});
		this.updateLabelPreview();
		field.inputRef.focus();
	};

	@action.bound
	findIndexOfDifference = (originalValue, modifiedValue) => {
		for (let i = 0; i < originalValue.length && i < modifiedValue.length; i++) {
			if (originalValue[i] !== modifiedValue[i]) {
				return i;
			}
		}

		return modifiedValue.length - 1;
	};

	@action.bound setInputs = (input, field) => {
		const key = field.key + 'Input';
		this[key] = input;
		this.onInputChangeUpdatePreview(field);
	};

	@action.bound
	onInputChangeUpdatePreview = field => {
		if (field == null) return;

		let idToUpdate = null;
		let valueToSet = null;
		if (field.key === 'attentionName') {
			idToUpdate = 'attentionName';
			valueToSet = field.value;
		}
		if (this.form.$('useCustomReturnAddress').value) {
			switch (field.key) {
				case 'returnName':
					idToUpdate = 'name';
					valueToSet = field.value;
					break;
				case 'returnPhone':
					idToUpdate = 'phone';
					valueToSet = field.value;
					break;
				case 'returnAddress':
					idToUpdate = field.key;
					valueToSet = field.value;
					break;
				case 'returnZip':
				case 'returnCity':
					idToUpdate = 'returnCityStateAndZip';
					valueToSet = `${this.form.$('returnCity').value} ${
						this.form.$('returnStateId').value ? this.stateDropdownStore.value.label : ''
					} ${this.form.$('returnZip').value}`;
					break;
				default:
					break;
			}
		} else {
			if (field.key == 'sellerName') {
				idToUpdate = 'name';
				valueToSet = field.value;
			} else if (field.key == 'sellerPhone') {
				idToUpdate = 'phone';
				valueToSet = field.value;
			}
		}

		this.updateSvgElementById(idToUpdate, valueToSet);
	};

	onCarrierTypeDropdownChange = async () => {
		await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms for SVG to load
		var svgElement = document.getElementById('svgDocumentId');
		if (svgElement) {
			this.updateLabelPreview();
		}
	};

	@action.bound
	onStateDropdownChange = stateDropdownValue => {
		if (!stateDropdownValue) return;
		let valueToSet = `${this.form.$('returnCity').value} ${stateDropdownValue.label} ${this.form.$('returnZip').value}`;
		this.updateSvgElementById('returnCityStateAndZip', valueToSet);
	};

	updateLabelPreview = () => {
		let svgFieldKeys = [];
		if (this.form.$('useCustomReturnAddress').value) {
			svgFieldKeys = ['returnName', 'returnPhone', 'attentionName', 'returnAddress', 'returnZip'];
		} else {
			svgFieldKeys = ['sellerName', 'sellerPhone', 'attentionName'];
			this.updateSvgElementById('returnCityStateAndZip', 'ATLANTA GA 30302');
			this.updateSvgElementById('returnAddress', '18TH C MAIN ROAD');
		}

		svgFieldKeys.forEach(fieldKey => {
			this.onInputChangeUpdatePreview(this.form.$(fieldKey));
		});

		this.form.$('customFieldInputs').value.forEach(customField => {
			this.updateSvgElementById(`customField${customField.fieldNumber}`, customField.value);
		});
	};

	updateSvgElementById(idToUpdate, valueToSet) {
		if (idToUpdate && valueToSet != null) {
			var svgElement = document.getElementById('svgDocumentId');
			if (svgElement && svgElement.contentDocument) {
				this.updatePositioning(svgElement);
				var textElementToUpdate = svgElement.contentDocument.getElementById(idToUpdate);
				if (textElementToUpdate) {
					textElementToUpdate.textContent = valueToSet;
				}
			}
		}
	}

	// Used because some elements are positioned differently on UPS and USPS label when attentionName is provided
	updatePositioning = svgElement => {
		let attentionNameElement = svgElement.contentDocument.getElementById('attentionName');
		let nameElement = svgElement.contentDocument.getElementById('name');
		let phoneElement = svgElement.contentDocument.getElementById('phone');
		let returnAddressElement = svgElement.contentDocument.getElementById('returnAddress');
		let returnCityStateAndZipElement = svgElement.contentDocument.getElementById('returnCityStateAndZip');

		if (
			attentionNameElement &&
			this.carrierTypeDropdownStore.value &&
			this.carrierTypeDropdownStore.value.label === carrierTypes.ups
		) {
			if (this.form.$('attentionName').value) {
				attentionNameElement.setAttribute('y', 12.655);
				nameElement.setAttribute('y', 41.655);
				phoneElement.setAttribute('y', 26.655);
				returnAddressElement.setAttribute('y', 54.655);
				returnCityStateAndZipElement.setAttribute('y', 68.655);
			} else {
				attentionNameElement.setAttribute('y', 0);
				nameElement.setAttribute('y', 21.655);
				phoneElement.setAttribute('y', 35.655);
				returnAddressElement.setAttribute('y', 49.655);
				returnCityStateAndZipElement.setAttribute('y', 63.655);
			}
		} else if (
			attentionNameElement &&
			this.carrierTypeDropdownStore.value &&
			this.carrierTypeDropdownStore.value.label === carrierTypes.usps
		) {
			if (this.form.$('attentionName').value) {
				attentionNameElement.setAttribute('y', 232.48);
				nameElement.setAttribute('y', 252.48);
				returnAddressElement.setAttribute('y', 273.48);
				returnCityStateAndZipElement.setAttribute('y', 294.48);
			} else {
				attentionNameElement.setAttribute('y', 0);
				nameElement.setAttribute('y', 239.48);
				returnAddressElement.setAttribute('y', 260.48);
				returnCityStateAndZipElement.setAttribute('y', 281.48);
			}
		}
	};

	@action.bound
	onCustomFieldDropdownBlur = async customFieldInput => {
		if (customFieldInput.$('displayDropdown').value) {
			customFieldInput.$('displayDropdown').value = false;
		}
	};

	@action.bound
	onCustomFieldDropdownChange = async (selectedValues, field) => {
		if (selectedValues && selectedValues.length > field.value.customFieldInputVariables.length) {
			// if a variable has been added
			const lastAdded = selectedValues[selectedValues.length - 1];
			this.onSelectChange(lastAdded, field);
		} else if (!selectedValues || selectedValues.length < field.value.customFieldInputVariables.length) {
			// if a variable has been removed
			let deletedVariable = field.value.customFieldInputVariables.find(
				v => !selectedValues || !selectedValues.some(val => val.extra.displayName === v.value)
			);
			if (deletedVariable) {
				const outputString = await this.deleteVariables([`{{${deletedVariable.value}}}`], field);
				field.$('previousValue').value = field.$('value').value;
				this.updateSvgElementById(`customField${field.value.fieldNumber}`, outputString);
			}
		}
	};
}

export default CustomLabelViewStore;



// WEBPACK FOOTER //
// ./src/modules/channel/stores/CustomLabelViewStore.js